import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { PieChart } from '@openx/components-core/lib/Charts/PieChart/PieChart';
import { palette } from '@openx/components-core/lib/theme/paletteV2';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DistributionSectionData } from '../types';

type DistributionSectionProps = {
  data: DistributionSectionData;
  isLoading: boolean;
  title: string;
};

export const DistributionSection = ({
  isLoading,
  data: { directness, media_type, channel, dsp_name },
  title,
}: DistributionSectionProps) => {
  const { t } = useTranslation();
  const noData = useMemo(() => {
    return (
      directness.every(({ value }) => !value) &&
      media_type.every(({ value }) => !value) &&
      channel.every(({ value }) => !value) &&
      dsp_name.every(({ value }) => !value)
    );
  }, [directness, media_type, channel, dsp_name]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, py: 3, px: 4 }} data-test="distribution-section">
      <Typography variant="h2" data-test="distribution-section-title">
        {title}
      </Typography>
      <Grid container>
        {noData && !isLoading ? (
          <Grid
            size={12}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            data-test="distribution-section-item"
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: 336,
                width: '100%',
                gap: 0.5,
                textAlign: 'center',
              }}
            >
              <Typography variant="body1Bold">{t('No data available')}</Typography>
              <Typography variant="body1" color={palette.text.secondary}>
                {t('Try adjusting filters or date range to see the results.')}
              </Typography>
            </Box>
          </Grid>
        ) : (
          <>
            <Grid
              size={{ lg: 3, xs: 12 }}
              data-test="distribution-section-item"
              sx={{
                borderRight: 1,
                borderColor: 'divider',
              }}
            >
              <PieChart
                title={t('Direct vs. Indirect')}
                data={directness}
                loading={isLoading}
                containerSx={{ mt: 4 }}
              />
            </Grid>
            <Grid
              size={{ lg: 3, xs: 12 }}
              data-test="distribution-section-item"
              sx={{
                borderRight: 1,
                borderColor: 'divider',
              }}
            >
              <PieChart title={t('Format')} data={media_type} loading={isLoading} containerSx={{ mt: 4 }} />
            </Grid>
            <Grid
              size={{ lg: 3, xs: 12 }}
              data-test="distribution-section-item"
              sx={{
                borderRight: 1,
                borderColor: 'divider',
              }}
            >
              <PieChart title={t('Channel')} data={channel} loading={isLoading} containerSx={{ mt: 4 }} />
            </Grid>
            <Grid size={{ lg: 3, xs: 12 }} data-test="distribution-section-item">
              <PieChart title={t('DSP')} data={dsp_name} loading={isLoading} containerSx={{ mt: 4 }} />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};
